import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  NotificationService,
  DataService,
  Asset
} from '@vendure/admin-ui/core';
import { AboutFormData, defaultAboutFormData } from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { safeJSONParse } from '../utils';
import { Observable } from 'rxjs';

@Component({
  template: `
    <vdr-card>
     <!-- Text (Rich-text Editor) -->
      <vdr-form-field [label]="'Text'" class="mb-2">
        <vdr-dynamic-form-input [control]="textControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <!-- Citation (Rich-text Editor) -->
      <vdr-form-field [label]="'Citation'" class="mb-2">
        <vdr-dynamic-form-input [control]="citationControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>

      <!-- Featured Asset -->
      <vdr-form-field [label]="'Featured Asset'">
        <vdr-dynamic-form-input
          [control]="featuredAssetControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class AboutFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };

  aboutFormData: AboutFormData;

  textControl = new FormControl();
  citationControl = new FormControl();
  featuredAssetControl = new UntypedFormControl();

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.aboutFormData = safeJSONParse<AboutFormData>(
      this.formControl.value,
      this.notificationService,
      defaultAboutFormData,
    )!;
    this.initializeFormControls();
    // Load featuredAsset
    if (this.aboutFormData?.featuredAssetId) {
      this.dataService.product
        .getAsset(this.aboutFormData.featuredAssetId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.featuredAssetControl.setValue(asset);
        });
    } else {
      this.featuredAssetControl.setValue(null);
    }
  }

  ngAfterViewInit() {
    this.textControl.valueChanges.subscribe(this.onTextChange);
    this.citationControl.valueChanges.subscribe(this.onCitationChange);
    this.featuredAssetControl.valueChanges.subscribe(this.onFeaturedAssetChange);
  }

  private initializeFormControls() {
    this.textControl.setValue(this.toTextValueJson(this.aboutFormData?.text));
    this.citationControl.setValue(this.toTextValueJson(this.aboutFormData?.citation));
  }

  private onTextChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.aboutFormData?.text) !== JSON.stringify(next)) {
      this.aboutFormData!.text = next;
      this.updateJsonString();
    }
  };

  private onCitationChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.aboutFormData?.citation) !== JSON.stringify(next)) {
      this.aboutFormData!.citation = next;
      this.updateJsonString();
    }
  };

  private onFeaturedAssetChange = (value: Asset) => {
    const featuredAssetId = value ? value.id : null;
    if (this.aboutFormData?.featuredAssetId !== featuredAssetId) {
      this.aboutFormData!.featuredAssetId = featuredAssetId;
      this.updateJsonString();
    }
  };

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private updateJsonString() {
    const jsonString = JSON.stringify(this.aboutFormData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
